import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, switchMap, take } from 'rxjs';
import {
  FeedbackDialogComponent,
  FeedbackDialogData,
  FeedbackDialogResult,
} from '../components/feedback-dialog/feedback-dialog.component';
import { EnvironmentService } from '../shared/environment.service';
import { FirebaseAuthService } from '../shared/firebase-auth.service';
import { ClientLoggingService } from '../shared/client-logging.service';
import { FeedbackService, SubmitFeedbackRequest, SubmitFeedbackResponse } from './feedback.service';

export interface ErrorReportContext {
  /** User-safe error text already displayed in the product. */
  summary: string;
  /** Stable application error classification. */
  errorCode?: string;
  /** Opaque server-side log correlation identifier. */
  correlationId?: string;
  /** Short allowlisted operation name, such as `chat-response`. */
  operation: string;
  occurredAt?: Date | string;
  projectId?: string;
  chatSessionId?: string;
  taskId?: string;
  /** Optional chat transcript. The user must opt in before it is sent. */
  chatLogJson?: string;
  /** Additional state selected by the caller. Values must already be safe to report. */
  uiState?: Record<string, unknown>;
}

/**
 * Opens and submits a prefilled private bug report for a displayed application error.
 *
 * The browser only sends an allowlisted diagnostic envelope. Server status metadata and
 * the dedicated feedback GitHub credential remain on the backend.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackReporterService {
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly feedbackService = inject(FeedbackService);
  private readonly authService = inject(FirebaseAuthService);
  private readonly environmentService = inject(EnvironmentService);

  private beforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;

  openErrorReport(context: ErrorReportContext): void {
    const dialogData = this.buildDialogData(context);
    const dialogRef = this.dialog.open(FeedbackDialogComponent, {
      data: dialogData,
      width: '560px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe((result: FeedbackDialogResult | undefined) => {
      if (result) {
        this.submit(result, dialogData);
      }
    });
  }

  private buildDialogData(context: ErrorReportContext): FeedbackDialogData {
    const firebaseProjectId = this.environmentService.getFirebaseConfig().projectId;
    const pageUrl = this.safePageUrl(window.location.href);
    const occurredAt = this.toIsoTimestamp(context.occurredAt);
    const diagnostics = {
      schemaVersion: 1,
      capturedAt: new Date().toISOString(),
      error: {
        code: this.optionalBounded(context.errorCode, 100),
        correlationId: this.optionalBounded(context.correlationId, 100),
        operation: this.requiredBounded(context.operation, 100),
        occurredAt,
      },
      page: {
        url: pageUrl,
        route: window.location.pathname,
      },
      environment: {
        firebaseProjectId: this.requiredBounded(firebaseProjectId, 200),
      },
      browser: {
        userAgent: this.requiredBounded(navigator.userAgent, 500),
        language: this.requiredBounded(navigator.language, 50),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      },
      project: {
        projectId: this.optionalBounded(context.projectId, 300),
      },
      chat: {
        sessionId: this.optionalBounded(context.chatSessionId, 200),
      },
      task: {
        taskId: this.optionalBounded(context.taskId, 200),
      },
      state: context.uiState ?? {},
    };

    const diagnosticItems = [
      context.errorCode ? `Error code ${context.errorCode}` : undefined,
      context.correlationId ? `Correlation reference ${context.correlationId}` : undefined,
      context.operation ? `Operation ${context.operation}` : undefined,
      context.projectId ? 'Current project identifier' : undefined,
      context.chatSessionId ? 'Chat session identifier' : undefined,
      'Page route, environment, browser version, viewport, and timestamp',
    ].filter((item): item is string => !!item);

    return {
      initialCategory: 'BUG',
      initialDescription: this.prefilledDescription(context.summary),
      diagnosticItems,
      chatLogJson: context.chatLogJson,
      chatSessionId: context.chatSessionId,
      pageUrl,
      uiStateJson: JSON.stringify(diagnostics, null, 2),
    };
  }

  private submit(result: FeedbackDialogResult, dialogData: FeedbackDialogData): void {
    this.snackBar.open('Submitting feedback...', undefined, { duration: 0 });
    this.addBeforeUnloadGuard();

    this.authService.loading$
      .pipe(
        filter(loading => !loading),
        take(1),
        switchMap(() => this.authService.user$.pipe(take(1)))
      )
      .subscribe(user => {
        const request: SubmitFeedbackRequest = {
          description: result.description,
          category: result.category,
          chatSessionId: dialogData.chatSessionId,
          chatLogJson: result.includeChatLog ? dialogData.chatLogJson : undefined,
          pageUrl: dialogData.pageUrl || this.safePageUrl(window.location.href),
          uiStateJson: dialogData.uiStateJson,
          userEmail: result.userConsent ? user?.email || 'authenticated-user' : undefined,
        };

        this.feedbackService.submitFeedback(request).subscribe({
          next: (response: SubmitFeedbackResponse) => {
            this.removeBeforeUnloadGuard();
            this.snackBar
              .open(`Feedback submitted! Issue #${response.issueNumber} created.`, 'View Issue', {
                duration: 8000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              })
              .onAction()
              .subscribe(() => {
                if (response.issueUrl?.startsWith('https://')) {
                  window.open(response.issueUrl, '_blank');
                }
              });
          },
          error: (error: { status: number; message: string; raw: unknown }) => {
            this.removeBeforeUnloadGuard();
            ClientLoggingService.log(
              'ERROR',
              `[FeedbackReporterService] Submission failed (HTTP ${error.status}):`,
              error.message
            );
            this.snackBar.open(error.message || 'Failed to submit feedback', 'Dismiss', {
              duration: 10000,
            });
          },
        });
      });
  }

  private prefilledDescription(summary: string): string {
    const safeSummary = this.requiredBounded(summary, 2000);
    return `What I was doing when this happened:\n\n\nDisplayed error:\n${safeSummary}`;
  }

  private safePageUrl(rawUrl: string): string {
    try {
      const url = new URL(rawUrl);
      return `${url.origin}${url.pathname}`;
    } catch {
      return window.location.pathname;
    }
  }

  private toIsoTimestamp(value?: Date | string): string | undefined {
    if (!value) {
      return undefined;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  private optionalBounded(value: string | undefined, maxLength: number): string | undefined {
    return value ? this.requiredBounded(value, maxLength) : undefined;
  }

  private requiredBounded(value: string, maxLength: number): string {
    return value
      .replace(/[\r\n\t]+/g, ' ')
      .trim()
      .slice(0, maxLength);
  }

  private addBeforeUnloadGuard(): void {
    this.beforeUnloadHandler = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private removeBeforeUnloadGuard(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
}
